import React from "react";
import moment from "moment";
import CommonSelect from "../../../../core/common/commonSelect";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import ImageSelectDropMenu from "../../../administration/imageSelectDropMenu";

interface FilterSectionProps {
  showFilters: boolean;
  configurationOptions: any[];
  selectedConfiguration: any;
  isLoadingConfigurations: boolean;
  selectedCompany: any;
  employeeOptions: any[];
  departmentOptions: any[];
  employees: any[];
  departments: any[];
  isEmployee: boolean;
  selectedStartDate: moment.Moment | null;
  selectedEndDate: moment.Moment | null;
  selectedEmployee: string;
  selectedDepartment: string;
  activeFiltersCount: number;
  onToggleFilters: () => void;
  onConfigurationSelection: (selectedOption: any) => void;
  onDateChange: (start: moment.Moment, end: moment.Moment) => void;
  onEmployeeSelection: (selectedOption: any) => void;
  onDepartmentSelection: (selectedOption: any) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  showFilters,
  configurationOptions,
  selectedConfiguration,
  isLoadingConfigurations,
  selectedCompany,
  employeeOptions,
  departmentOptions,
  employees,
  departments,
  isEmployee,
  selectedStartDate,
  selectedEndDate,
  selectedEmployee,
  selectedDepartment,
  activeFiltersCount,
  onToggleFilters,
  onConfigurationSelection,
  onDateChange,
  onEmployeeSelection,
  onDepartmentSelection,
  onApplyFilters,
  onClearFilters,
}) => {
  const ClearFilterBtn = ({
    onClick,
    title = "Clear this filter",
  }: {
    onClick: () => void;
    title?: string;
  }) => (
    <button
      className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
      onClick={onClick}
      title={title}
      style={{ fontSize: "12px" }}>
      <i className="ti ti-x fs-13 me-1" />
      Clear
    </button>
  );

  return (
    <div
      className={`dashboard-filters ${
        showFilters ? "show-filters" : "hide-filters"
      }`}>
      <div className="d-flex justify-content-between align-items-center p-3">
        <h5 className="mb-0">
          <i className="ti ti-filter me-2 text-primary" />
          Dashboard Filters
        </h5>
        <div className="d-flex gap-3 align-items-center">
          <div
            style={{ width: "250px" }}
            title="Choose your saved dashboard configurations">
            <CommonSelect
              className="select"
              options={configurationOptions}
              defaultValue={
                selectedConfiguration
                  ? configurationOptions.find(
                      (config: any) =>
                        config.value === selectedConfiguration.id.toString()
                    )
                  : undefined
              }
              onChange={onConfigurationSelection}
              placeHolder={
                isLoadingConfigurations
                  ? "Loading configurations..."
                  : "Select Saved Dashboard"
              }
              isDisabled={isLoadingConfigurations || !selectedCompany}
            />
          </div>

          <button
            style={{ minWidth: "150px" }}
            className="btn btn-md btn-secondary py-2 px-3"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFilters();
            }}
            title={showFilters ? "Hide Filters" : "Show Filters"}>
            <i
              className={`ti ti-chevron-${showFilters ? "up" : "down"} me-1`}
            />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mt-0 border-top border-2">
          <div className="row g-3 justify-content-end p-3">
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <label className="form-label">Date Range</label>
                {(selectedStartDate || selectedEndDate) && (
                  <ClearFilterBtn
                    onClick={() => onDateChange(null as any, null as any)}
                  />
                )}
              </div>

              <div className="d-flex align-items-center">
                <PredefinedDateRanges
                  onDateChange={onDateChange}
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  dateFormat="DD MMM YYYY"
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="d-flex gap-3">
                <label className="form-label">Employees</label>
                {selectedEmployee && (
                  <ClearFilterBtn onClick={() => onEmployeeSelection(null)} />
                )}
              </div>

              <ImageSelectDropMenu
                options={employeeOptions}
                onChange={onEmployeeSelection}
                defaultValue={
                  selectedEmployee
                    ? employeeOptions.find(
                        (emp: any) => emp.value === selectedEmployee
                      )
                    : undefined
                }
                placeHolder="Select Employee"
                isDisabled={employees.length === 0 || isEmployee}
              />
            </div>

            <div className="col-md-3">
              <div className="d-flex gap-3">
                <label className="form-label">Departments</label>
                {selectedDepartment && (
                  <ClearFilterBtn onClick={() => onDepartmentSelection(null)} />
                )}
              </div>
              <CommonSelect
                options={departmentOptions}
                placeHolder="Select departments"
                defaultValue={
                  selectedDepartment
                    ? departmentOptions.find(
                        (dept: any) => dept.value === selectedDepartment
                      )
                    : undefined
                }
                onChange={onDepartmentSelection}
                isDisabled={departments.length === 0 || isEmployee}
              />
            </div>

            <div className="col-md-2">
              <div className="mb-1 d-flex align-items-center">
                <button
                  className="btn btn-md btn-primary w-100"
                  onClick={onApplyFilters}>
                  <i className="ti ti-checks me-1 fs-14 fw-medium" />
                  Apply Filters
                </button>
              </div>
              <div className="mb-1 d-flex align-items-center">
                <button
                  className="btn btn-md btn-outline-secondary w-100"
                  onClick={onClearFilters}
                  disabled={activeFiltersCount === 0}>
                  <i className="ti ti-filter-off me-1 fs-14 fw-medium" />
                  Clear {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSection;
